/**
 * Sport-only bottom nav gestures (no iframe overlay — scrolling stays intact).
 * - Swipe down on the tab bar → hide
 * - Thin bottom peek → swipe up / tap → show
 * Nav overlays the sportsbook (no reserved padding) so hide never leaves a black gap.
 */
import { useEffect, useRef } from 'react';

const DRAG_THRESHOLD = 14;

export default function SportNavChrome({ active, hidden, onHiddenChange }) {
  const startYRef = useRef(0);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);

  // Swipe-down on visible tab bar → hide (does not cover iframe)
  useEffect(() => {
    if (!active || hidden) return undefined;
    const bar = document.querySelector('.tab-bar-container');
    if (!bar) return undefined;

    const onStart = (event) => {
      const y = event.touches?.[0]?.clientY;
      if (y == null) return;
      startYRef.current = y;
      draggingRef.current = true;
      movedRef.current = false;
    };
    const onMove = (event) => {
      if (!draggingRef.current || !event.touches?.[0]) return;
      const dy = event.touches[0].clientY - startYRef.current; // finger down => positive
      if (dy > DRAG_THRESHOLD) {
        movedRef.current = true;
        draggingRef.current = false;
        onHiddenChange(true);
      }
    };
    const onEnd = (event) => {
      draggingRef.current = false;
      if (movedRef.current) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    bar.addEventListener('touchstart', onStart, { passive: true });
    bar.addEventListener('touchmove', onMove, { passive: true });
    bar.addEventListener('touchend', onEnd, { passive: false });
    bar.addEventListener('touchcancel', onEnd, { passive: false });
    return () => {
      bar.removeEventListener('touchstart', onStart);
      bar.removeEventListener('touchmove', onMove);
      bar.removeEventListener('touchend', onEnd);
      bar.removeEventListener('touchcancel', onEnd);
    };
  }, [active, hidden, onHiddenChange]);

  // Intentionally no header-tap reveal: browsing sport + header controls was
  // accidentally bringing the site bottom nav back. Use peek pill / swipe only.

  if (!active || !hidden) return null;

  const onPeekStart = (event) => {
    const y = event.touches?.[0]?.clientY;
    if (y == null) return;
    startYRef.current = y;
    draggingRef.current = true;
    movedRef.current = false;
  };

  const onPeekMove = (event) => {
    if (!draggingRef.current || !event.touches?.[0]) return;
    const dy = startYRef.current - event.touches[0].clientY; // finger up => positive
    if (dy > DRAG_THRESHOLD) {
      movedRef.current = true;
      draggingRef.current = false;
      onHiddenChange(false);
    }
  };

  const onPeekEnd = () => {
    draggingRef.current = false;
  };

  const onPeekTap = () => {
    onHiddenChange(false);
  };

  return (
    <button
      type="button"
      className="sport-nav-peek"
      aria-label="Menüyü göster"
      onClick={onPeekTap}
      onTouchStart={onPeekStart}
      onTouchMove={onPeekMove}
      onTouchEnd={onPeekEnd}
      onTouchCancel={onPeekEnd}
    >
      <span className="sport-nav-peek__pill" />
    </button>
  );
}
